import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Pie, Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import Spinner from './Spinner';
import './Inventario.css';

const Inventario = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortType, setSortType] = useState(null); // Estado para tipo de ordenamiento de productos
  const [categorySortType, setCategorySortType] = useState(null); // Estado para tipo de ordenamiento de categorías
  const [sortedCategories, setSortedCategories] = useState([]); // Estado para categorías ordenadas
  const [viewMode, setViewMode] = useState('products'); // 'products' o 'categories'
  const [pieModalOpen, setPieModalOpen] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const productsCollection = await getDocs(collection(db, 'products'));
      const productsData = productsCollection.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        stock: Number(doc.data().stock) || 0,
        costPrice: Number(doc.data().costPrice) || 0,
        excludeFromStock: doc.data().excludeFromStock ?? false
      }));
      setProducts(productsData);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (!pieModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPieModalOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [pieModalOpen]);

  useEffect(() => {
    const sortCategories = () => {
      const productsThatCount = products.filter(p => !p.excludeFromStock);
      const categoryMap = productsThatCount.reduce((acc, product) => {
        const { category, stock, costPrice } = product;
        if (!acc[category]) acc[category] = { stock: 0, cost: 0 };
        acc[category].stock += stock;
        acc[category].cost += stock * costPrice;
        return acc;
      }, {});

      const categories = Object.keys(categoryMap).map(category => ({
        category,
        stock: categoryMap[category].stock,
        cost: categoryMap[category].cost
      }));

      const sorted = categorySortType === 'quantity'
        ? categories.sort((a, b) => b.stock - a.stock)
        : categories.sort((a, b) => b.cost - a.cost);

      setSortedCategories(sorted);
    };

    if (products.length > 0) {
      sortCategories();
    }
  }, [products, categorySortType]);

  const formatNumber = (number) => {
    if (typeof number !== 'number' || isNaN(number)) {
      return '0,00';
    }
    return number
      .toFixed(2) // Mantiene dos decimales
      .replace('.', ',') // Reemplaza el punto decimal por una coma
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // Agrega puntos como separadores de miles
  };

  const formatQuantity = (number) => {
    if (typeof number !== 'number' || isNaN(number)) {
      return '0';
    }
    return Math.round(number).toLocaleString('es-AR', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  };

  

  // Solo productos que computan stock para gráficos y totales
  const productsThatCount = products.filter(p => !p.excludeFromStock);
  const totalStock = productsThatCount.reduce((total, product) => total + product.stock, 0);
  const totalCost = productsThatCount.reduce((total, product) => total + (product.stock * product.costPrice), 0);

  // Función para obtener datos ordenados de mayor a menor para productos
  const getSortedProductData = () => {
    const dataArr = productsThatCount.map(product => ({
      label: product.model,
      value: sortType === 'cost' ? product.stock * product.costPrice : product.stock,
      quantity: product.stock,
      cost: product.stock * product.costPrice,
      color: '#36A2EB' // color por defecto, se sobreescribe abajo
    }));
    // Asignar colores cíclicamente
    const colors = [
      '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
    ];
    dataArr.forEach((d, i) => d.color = colors[i % colors.length]);
    // Ordenar de mayor a menor
    dataArr.sort((a, b) => b.value - a.value);
    return dataArr;
  };

  // Función para obtener datos ordenados de mayor a menor para categorías
  const getSortedCategoryData = () => {
    const dataArr = sortedCategories.map(category => ({
      label: category.category,
      value: categorySortType === 'cost' ? category.cost : category.stock,
      quantity: category.stock,
      cost: category.cost,
      color: '#36A2EB'
    }));
    const colors = [
      '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
    ];
    dataArr.forEach((d, i) => d.color = colors[i % colors.length]);
    dataArr.sort((a, b) => b.value - a.value);
    return dataArr;
  };

  const getPieData = () => {
    const sorted = getSortedProductData();
    return {
      labels: sorted.map(d => d.label),
      datasets: [
        {
          data: sorted.map(d => d.value),
          backgroundColor: sorted.map(d => d.color),
          tooltipQty: sorted.map(d => d.quantity),
          tooltipCost: sorted.map(d => d.cost),
        },
      ],
    };
  };

  const getCategoryPieData = () => {
    const sorted = getSortedCategoryData();
    return {
      labels: sorted.map(d => d.label),
      datasets: [
        {
          data: sorted.map(d => d.value),
          backgroundColor: sorted.map(d => d.color),
          tooltipQty: sorted.map(d => d.quantity),
          tooltipCost: sorted.map(d => d.cost),
        },
      ],
    };
  };

  const sortProducts = (type) => {
    const sorted = [...products];
    if (type === 'quantity') {
      sorted.sort((a, b) => {
        const aVal = (a.excludeFromStock ? 0 : a.stock) / totalStock;
        const bVal = (b.excludeFromStock ? 0 : b.stock) / totalStock;
        return (bVal * 100) - (aVal * 100);
      });
    } else if (type === 'cost') {
      sorted.sort((a, b) => {
        const aVal = a.excludeFromStock ? 0 : (a.stock * a.costPrice / totalCost);
        const bVal = b.excludeFromStock ? 0 : (b.stock * b.costPrice / totalCost);
        return (bVal * 100) - (aVal * 100);
      });
    }
    setProducts(sorted); // Actualizar el estado products
    setSortType(type);
  };

  if (loading) return <Spinner />;

  // Configuración dinámica según vista
  const isProducts = viewMode === 'products';
  const chartTitle = isProducts
    ? (sortType === 'cost' ? 'Precio de Costo' : 'Cantidad de Artículos')
    : (categorySortType === 'cost' ? 'Costo por Categoría' : 'Cantidad por Categoría');
  const pieData = isProducts ? getPieData() : getCategoryPieData();
  const pieDs = pieData.datasets[0];
  const barData = {
    labels: pieData.labels,
    datasets: [
      {
        label: isProducts
          ? (sortType === 'cost' ? 'Monto al Costo' : 'Cantidad')
          : (categorySortType === 'cost' ? 'Monto al Costo' : 'Cantidad'),
        data: pieDs.data,
        backgroundColor: pieDs.backgroundColor,
        tooltipQty: pieDs.tooltipQty,
        tooltipCost: pieDs.tooltipCost,
      },
    ],
  };
  const tableHeaders = isProducts
    ? ['Producto', 'Cantidad', '% Cantidad', 'Monto al Costo', '% Costo']
    : ['Categoría', 'Cantidad', '% Cantidad', 'Monto al Costo', '% Costo'];
  const tableRows = isProducts
    ? products.map(product => {
        const excluded = product.excludeFromStock === true;
        const quantityPercentage = excluded ? 0 : totalStock > 0 ? (product.stock / totalStock) * 100 : 0;
        const costValue = excluded ? 0 : product.stock * product.costPrice;
        const costPercentage = excluded ? 0 : totalCost > 0 ? (costValue / totalCost) * 100 : 0;
        return [
          product.model + (excluded ? ' (no computa)' : ''),
          formatQuantity(product.stock),
          (excluded ? '—' : quantityPercentage.toFixed(2) + '%'),
          '$' + formatNumber(costValue),
          (excluded ? '—' : costPercentage.toFixed(2) + '%')
        ];
      })
    : sortedCategories.map(({ category, stock, cost }) => {
        const quantityPercentage = ((stock / totalStock) * 100).toFixed(2);
        const costPercentage = ((cost / totalCost) * 100).toFixed(2);
        return [
          category,
          formatQuantity(stock),
          quantityPercentage + '%',
          '$' + formatNumber(cost),
          costPercentage + '%'
        ];
      });
  const tableFooter = isProducts
    ? ['Total', formatQuantity(totalStock), '100%', '$' + formatNumber(totalCost), '100%']
    : ['Total', formatQuantity(totalStock), '100%', '$' + formatNumber(totalCost), '100%'];

  const inventoryTooltipCallbacks = {
    label: (context) => {
      const ds = context.dataset;
      const i = context.dataIndex;
      const qty = ds.tooltipQty?.[i];
      const cost = ds.tooltipCost?.[i];
      if (qty == null || cost == null) {
        return context.formattedValue ?? '';
      }
      return [
        `Cantidad: ${formatQuantity(qty)}`,
        `Monto al costo: $${formatNumber(cost)}`,
      ];
    },
  };

  const pieChartOptions = {
    elements: {
      arc: { borderWidth: 0 },
    },
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: inventoryTooltipCallbacks },
    },
  };

  const pieChartModalOptions = {
    ...pieChartOptions,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: inventoryTooltipCallbacks },
    },
  };

  const barChartOptions = {
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: inventoryTooltipCallbacks },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: { y: { beginAtZero: true } },
  };

  return (
    <div className="inventario">
      <h2 className="inventario-heading inventario-heading-h2">Inventario</h2>

      <div className="inventario-chart-section">
        <div className="inventario-chart-container">
          <div className="chart-controls">
            <button
              className={`sort-btn ${viewMode === 'products' ? 'active' : ''}`}
              onClick={() => setViewMode('products')}
            >
              Ver por Productos
            </button>
            <button
              className={`sort-btn ${viewMode === 'categories' ? 'active' : ''}`}
              onClick={() => setViewMode('categories')}
            >
              Ver por Categorías
            </button>
          </div>
          <h3 className="chart-title">{chartTitle}</h3>
          <div className="inventario-charts-row">
            <div className="inventario-pie-block">
              <button
                type="button"
                className="inventario-pie-expand-btn"
                onClick={() => setPieModalOpen(true)}
                aria-haspopup="dialog"
              >
                Ampliar gráfico
              </button>
              <div className="inventario-pie-chart">
                <Pie data={pieData} options={pieChartOptions} />
              </div>
            </div>
            <div className="inventario-bar-chart">
              <Bar data={barData} options={barChartOptions} />
            </div>
          </div>
        </div>
        <div className="sort-buttons">
          {isProducts ? (
            <>
              <button onClick={() => sortProducts('quantity')} className={`sort-btn ${sortType === 'quantity' ? 'active' : ''}`}>Ordenar por % Cantidad</button>
              <button onClick={() => sortProducts('cost')} className={`sort-btn ${sortType === 'cost' ? 'active' : ''}`}>Ordenar por % Costo</button>
            </>
          ) : (
            <>
              <button onClick={() => setCategorySortType('quantity')} className={`sort-btn ${categorySortType === 'quantity' ? 'active' : ''}`}>Ordenar por % Cantidad</button>
              <button onClick={() => setCategorySortType('cost')} className={`sort-btn ${categorySortType === 'cost' ? 'active' : ''}`}>Ordenar por % Costo</button>
            </>
          )}
        </div>
        <h3 className="inventario-heading inventario-heading-h3">
          {isProducts ? 'Detalles del Inventario' : 'Detalles por Categoría'}
        </h3>
        <div className="inventory-table-container">
          <table className="inventory-table">
            <thead>
              <tr>
                {tableHeaders.map((header, i) => <th key={i}>{header}</th>)}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => <td key={j}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                {tableFooter.map((cell, i) => <td key={i}>{cell}</td>)}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {pieModalOpen &&
        createPortal(
          <div
            className="inventario-pie-modal-backdrop"
            onClick={() => setPieModalOpen(false)}
            role="presentation"
          >
            <div
              className="inventario-pie-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="inventario-pie-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="inventario-pie-modal-header">
                <h3 id="inventario-pie-modal-title" className="inventario-pie-modal-title">
                  {chartTitle}
                </h3>
                <button
                  type="button"
                  className="inventario-pie-modal-close"
                  onClick={() => setPieModalOpen(false)}
                  aria-label="Cerrar"
                >
                  ×
                </button>
              </div>
              <div className="inventario-pie-modal-body">
                <div className="inventario-pie-modal-chart">
                  <Pie data={pieData} options={pieChartModalOptions} />
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Inventario;







